import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Space,
  Modal,
  message,
  Divider,
  Alert,
  Tag,
  List,
  Avatar,
  Spin
} from 'antd';
import {
  CreditCardOutlined,
  DollarOutlined,
  InfoCircleOutlined,
  PayCircleOutlined,
  BankOutlined,
  MobileOutlined,
  QrcodeOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const MerchantPayments = () => {
  const [form] = Form.useForm();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/merchant/payment-methods');
      setPaymentMethods(response.data.payment_methods);
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      message.error('Gagal memuat metode pembayaran');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const numericAmount = Number(amount);
    if (isNaN(numericAmount)) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(numericAmount);
  };

  const calculateFee = (amount, method) => {
    if (!amount || !method) return 0;
    return (amount * method.fee_percentage / 100) + method.fee_fixed;
  };

  const calculateTotal = (amount, method) => {
    if (!amount || !method) return amount || 0;
    return amount + calculateFee(amount, method);
  };

  const getMethodIcon = (provider) => {
    const iconMap = {
      'virtual_account': <BankOutlined style={{ fontSize: '24px' }} />,
      'ewallet': <MobileOutlined style={{ fontSize: '24px' }} />,
      'qris': <QrcodeOutlined style={{ fontSize: '24px' }} />,
      'credit_card': <CreditCardOutlined style={{ fontSize: '24px' }} />
    };
    return iconMap[provider] || <PayCircleOutlined style={{ fontSize: '24px' }} />;
  };

  const getProviderColor = (provider) => {
    const colorMap = {
      'virtual_account': '#1890ff',
      'ewallet': '#52c41a',
      'qris': '#722ed1',
      'credit_card': '#fa8c16'
    };
    return colorMap[provider] || '#1890ff';
  };

  const handleCreatePayment = async (values) => {
    try {
      setCreateLoading(true);

      const paymentData = {
        ...values,
        payment_method_id: selectedMethod.id
      };

      const response = await axios.post('/api/merchant/create-payment', paymentData);

      setPaymentResult(response.data);
      setPaymentModalVisible(true);
      form.resetFields();
      setSelectedMethod(null);

      message.success('Pembayaran berhasil dibuat!');

    } catch (error) {
      console.error('Create payment error:', error);
      const errorMessage = error.response?.data?.error || 'Gagal membuat pembayaran';
      message.error(errorMessage);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    form.setFieldsValue({
      payment_method_id: method.id
    });
  };

  const watchedAmount = form.getFieldValue('amount');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>Pembayaran</Title>
        <Text type="secondary">Buat dan kelola pembayaran untuk pelanggan Anda</Text>
      </div>

      <Row gutter={24}>
        {/* Payment Form */}
        <Col xl={12} lg={24} md={24} sm={24} xs={24}>
          <Card title="Buat Pembayaran Baru" className="mb-6">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleCreatePayment}
            >
              <Form.Item
                label="Deskripsi Pembayaran"
                name="description"
                rules={[{ required: true, message: 'Deskripsi diperlukan!' }]}
              >
                <Input
                  placeholder="Contoh: Pembayaran untuk produk ABC"
                  size="large"
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Jumlah"
                    name="amount"
                    rules={[
                      { required: true, message: 'Jumlah diperlukan!' },
                      { type: 'number', min: 1000, message: 'Minimal Rp 1.000' }
                    ]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      size="large"
                      formatter={value => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={value => value.replace(/Rp\s?|(,*)/g, '')}
                      placeholder="10000"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Order ID (Opsional)"
                    name="merchant_order_id"
                  >
                    <Input
                      placeholder="ORDER123"
                      size="large"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Catatan Tambahan (Opsional)"
                name="notes"
              >
                <TextArea
                  rows={3}
                  placeholder="Catatan untuk pembayaran ini..."
                />
              </Form.Item>

              {selectedMethod && watchedAmount && (
                <div className="mb-4">
                  <Alert
                    message="Ringkasan Pembayaran"
                    description={
                      <div>
                        <div className="flex justify-between">
                          <span>Jumlah:</span>
                          <span>{formatCurrency(watchedAmount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Fee ({selectedMethod.fee_percentage}% + {formatCurrency(selectedMethod.fee_fixed)}):</span>
                          <span>{formatCurrency(calculateFee(watchedAmount, selectedMethod))}</span>
                        </div>
                        <Divider style={{ margin: '8px 0' }} />
                        <div className="flex justify-between font-bold">
                          <span>Total:</span>
                          <span style={{ color: '#1890ff' }}>
                            {formatCurrency(calculateTotal(watchedAmount, selectedMethod))}
                          </span>
                        </div>
                      </div>
                    }
                    type="info"
                    showIcon
                  />
                </div>
              )}

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={createLoading}
                  disabled={!selectedMethod}
                  icon={<PayCircleOutlined />}
                >
                  {createLoading ? 'Membuat Pembayaran...' : 'Buat Pembayaran'}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Payment Methods */}
        <Col xl={12} lg={24} md={24} sm={24} xs={24}>
          <Card title="Pilih Metode Pembayaran">
            <List
              dataSource={paymentMethods}
              renderItem={(method) => (
                <List.Item
                  style={{
                    padding: '16px',
                    border: selectedMethod?.id === method.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    cursor: 'pointer',
                    backgroundColor: selectedMethod?.id === method.id ? '#f6ffed' : 'white'
                  }}
                  onClick={() => handleMethodSelect(method)}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        style={{
                          backgroundColor: getProviderColor(method.provider),
                          width: 48,
                          height: 48
                        }}
                        icon={getMethodIcon(method.provider)}
                      />
                    }
                    title={
                      <Space>
                        <Text strong>{method.method_name}</Text>
                        {!method.is_active && (
                          <Tag color="red">Tidak Aktif</Tag>
                        )}
                      </Space>
                    }
                    description={
                      <div>
                        <Text type="secondary">{method.description}</Text>
                        <br />
                        <Space>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Fee: {method.fee_percentage}% + {formatCurrency(method.fee_fixed)}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Min: {formatCurrency(method.min_amount)}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Max: {formatCurrency(method.max_amount)}
                          </Text>
                        </Space>
                      </div>
                    }
                  />
                </List.Item>
              )}
              locale={{ emptyText: 'Tidak ada metode pembayaran yang tersedia' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Payment Instructions */}
      <Row gutter={24} className="mt-6">
        <Col span={24}>
          <Card title="Panduan Pembayaran">
            <Row gutter={16}>
              <Col span={8}>
                <div className="text-center p-4">
                  <CreditCardOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
                  <Title level={4}>1. Pilih Metode</Title>
                  <Paragraph type="secondary">
                    Pilih metode pembayaran yang sesuai dengan kebutuhan pelanggan Anda
                  </Paragraph>
                </div>
              </Col>
              <Col span={8}>
                <div className="text-center p-4">
                  <DollarOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
                  <Title level={4}>2. Isi Detail</Title>
                  <Paragraph type="secondary">
                    Masukkan jumlah pembayaran dan deskripsi yang jelas untuk pelanggan
                  </Paragraph>
                </div>
              </Col>
              <Col span={8}>
                <div className="text-center p-4">
                  <PayCircleOutlined style={{ fontSize: '48px', color: '#722ed1', marginBottom: '16px' }} />
                  <Title level={4}>3. Buat & Bagikan</Title>
                  <Paragraph type="secondary">
                    Buat pembayaran dan bagikan informasi kepada pelanggan untuk menyelesaikan transaksi
                  </Paragraph>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Payment Result Modal */}
      <Modal
        title="Pembayaran Berhasil Dibuat"
        open={paymentModalVisible}
        onCancel={() => {
          setPaymentModalVisible(false);
          setPaymentResult(null);
        }}
        footer={null}
        width={600}
      >
        {paymentResult && (
          <div>
            <Alert
              message="Pembayaran berhasil dibuat!"
              description="Silakan bagikan informasi di bawah ini kepada pelanggan untuk menyelesaikan pembayaran."
              type="success"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Card>
              <div className="text-center mb-4">
                <Title level={3} style={{ color: '#1890ff' }}>
                  {formatCurrency(paymentResult.transaction?.total_amount)}
                </Title>
                <Text type="secondary">Total Pembayaran</Text>
              </div>

              <Divider />

              <div className="space-y-3">
                <div className="flex justify-between">
                  <Text strong>Transaction ID:</Text>
                  <Text copyable style={{ fontFamily: 'monospace' }}>
                    {paymentResult.transaction?.transaction_id}
                  </Text>
                </div>
                <div className="flex justify-between">
                  <Text strong>Jumlah:</Text>
                  <Text>{formatCurrency(paymentResult.transaction?.amount)}</Text>
                </div>
                <div className="flex justify-between">
                  <Text strong>Fee:</Text>
                  <Text>{formatCurrency(paymentResult.transaction?.fee)}</Text>
                </div>
                <div className="flex justify-between">
                  <Text strong>Metode Pembayaran:</Text>
                  <Text>{paymentResult.transaction?.payment_method}</Text>
                </div>
                <div className="flex justify-between">
                  <Text strong>Status:</Text>
                  <Tag color="orange">PENDING</Tag>
                </div>
                <div className="flex justify-between">
                  <Text strong>Berlaku Hingga:</Text>
                  <Text>{new Date(paymentResult.transaction?.expires_at).toLocaleString('id-ID')}</Text>
                </div>
              </div>

              <Divider />

              <Space className="w-full justify-center">
                <Button type="primary" icon={<InfoCircleOutlined />}>
                  Lihat Detail
                </Button>
                <Button icon={<CreditCardOutlined />}>
                  Bagikan ke Pelanggan
                </Button>
              </Space>
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MerchantPayments;